import { inflate } from "pako"

const PNG_SIG = [137, 80, 78, 71, 13, 10, 26, 10]

function paethPredictor(a: number, b: number, c: number): number {
    const p = a + b - c
    const pa = Math.abs(p - a)
    const pb = Math.abs(p - b)
    const pc = Math.abs(p - c)
    if (pa <= pb && pa <= pc) return a
    if (pb <= pc) return b
    return c
}

/**
 * Decodes a 16-bit grayscale PNG to a big-endian Uint16Array.
 * Uses pako for zlib decompression — no Node.js dependencies.
 */
export function decodePng16Bit(buffer: ArrayBuffer): {
    width: number
    height: number
    data: Uint16Array
} {
    const bytes = new Uint8Array(buffer)
    const view = new DataView(buffer)

    for (let i = 0; i < 8; i++) {
        if (bytes[i] !== PNG_SIG[i]) throw new Error("Not a valid PNG file")
    }

    let offset = 8
    let width = 0
    let height = 0
    const idatParts: Uint8Array[] = []

    while (offset < bytes.length) {
        const length = view.getUint32(offset, false)
        const type = String.fromCharCode(
            bytes[offset + 4],
            bytes[offset + 5],
            bytes[offset + 6],
            bytes[offset + 7]
        )

        if (type === "IHDR") {
            width = view.getUint32(offset + 8, false)
            height = view.getUint32(offset + 12, false)
            const bitDepth = bytes[offset + 16]
            const colorType = bytes[offset + 17]
            if (bitDepth !== 16 || colorType !== 0) {
                throw new Error(
                    `Expected 16-bit grayscale PNG, got bitDepth=${bitDepth} colorType=${colorType}`
                )
            }
        } else if (type === "IDAT") {
            idatParts.push(bytes.slice(offset + 8, offset + 8 + length))
        } else if (type === "IEND") {
            break
        }

        offset += 12 + length
    }

    if (width === 0 || height === 0) throw new Error("PNG IHDR not found")

    // Concatenate and decompress all IDAT chunks
    let totalLen = 0
    for (const p of idatParts) totalLen += p.length
    const idat = new Uint8Array(totalLen)
    let pos = 0
    for (const p of idatParts) {
        idat.set(p, pos)
        pos += p.length
    }
    const raw = inflate(idat)

    // Reconstruct scan lines. Each row: 1 filter byte + width*2 pixel bytes.
    const bpp = 2
    const stride = width * bpp
    const recon = new Uint8Array(height * stride)
    let srcPos = 0

    for (let y = 0; y < height; y++) {
        const filterType = raw[srcPos++]
        const dstRow = y * stride
        const prevRow = (y - 1) * stride

        for (let x = 0; x < stride; x++) {
            const rawByte = raw[srcPos + x]
            const a = x >= bpp ? recon[dstRow + x - bpp] : 0
            const b = y > 0 ? recon[prevRow + x] : 0
            const c = x >= bpp && y > 0 ? recon[prevRow + x - bpp] : 0

            switch (filterType) {
                case 0:
                    recon[dstRow + x] = rawByte
                    break
                case 1:
                    recon[dstRow + x] = (rawByte + a) & 0xff
                    break
                case 2:
                    recon[dstRow + x] = (rawByte + b) & 0xff
                    break
                case 3:
                    recon[dstRow + x] = (rawByte + Math.floor((a + b) / 2)) & 0xff
                    break
                case 4:
                    recon[dstRow + x] = (rawByte + paethPredictor(a, b, c)) & 0xff
                    break
                default:
                    throw new Error(`Unknown PNG filter type: ${filterType}`)
            }
        }
        srcPos += stride
    }

    // Pack byte pairs (big-endian) into Uint16Array
    const pixels = new Uint16Array(width * height)
    for (let i = 0; i < width * height; i++) {
        pixels[i] = (recon[i * 2] << 8) | recon[i * 2 + 1]
    }

    return { width, height, data: pixels }
}
